import { authedFetch, UnauthorizedError } from '@apis/auth';

// Mirrors backend core/help/types.ts (the summary/detail/search shapes).
export type HelpGroup = 'home' | 'overview' | 'operations' | 'planning' | 'engineering' | 'performance' | 'platform' | 'admin' | 'other';

/** The guide languages the Help Center serves; the UI language picks one. */
export type HelpLanguage = 'en' | 'ja';

export interface IHelpGuideSummary {
  slug: string;
  /** The language this version actually is: the user's when it exists, else the English fallback. */
  language: HelpLanguage;
  parentSlug: string | null;
  title: string;
  group: HelpGroup;
  iconKey: string;
  order: number;
  summary: string;
  minutes: number;
  sectionTitles: string[];
  sectionCount: number;
  updatedAt: string;
}

export interface IHelpGuideDetail extends IHelpGuideSummary {
  html: string;
  headings: { id: string; text: string; level: 2 | 3 }[];
}

/** One guide as the hub searches it in the browser: its plain text, and where each heading starts in it. */
export interface IHelpSearchEntry {
  slug: string;
  language: HelpLanguage;
  text: string;
  headings: { id: string; text: string; level: 2 | 3; offset: number }[];
}

async function jsonOrThrow<T>(res: Response, ctx: string): Promise<T> {
  if (res.ok) return (await res.json()) as T;
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`${ctx} failed: ${res.status}`);
}

export async function fetchHelpGuides(language: HelpLanguage, signal?: AbortSignal): Promise<IHelpGuideSummary[]> {
  return (await jsonOrThrow<{ guides: IHelpGuideSummary[] }>(await authedFetch(`/api/help/guides?lang=${language}`, { signal }), 'fetch guides')).guides;
}

export async function fetchHelpGuide(slug: string, language: HelpLanguage, signal?: AbortSignal): Promise<IHelpGuideDetail> {
  return jsonOrThrow(await authedFetch(`/api/help/guides/${encodeURIComponent(slug)}?lang=${language}`, { signal }), 'fetch guide');
}

export async function fetchHelpSearchIndex(language: HelpLanguage, signal?: AbortSignal): Promise<IHelpSearchEntry[]> {
  return (await jsonOrThrow<{ guides: IHelpSearchEntry[] }>(await authedFetch(`/api/help/search-index?lang=${language}`, { signal }), 'fetch search index')).guides;
}

export interface IHelpSyncResult {
  fetched: number;
  removed: number;
  failed: number;
}

/** Pulls the guides from their source now — the only way they sync (Trusstor admins only). */
export async function syncHelpGuides(): Promise<IHelpSyncResult> {
  return jsonOrThrow(await authedFetch('/api/help/sync', { method: 'POST' }), 'sync guides');
}
